import { useTranslation } from "react-i18next";
import { Clock } from "lucide-react";

import { useAuth } from "../lib/auth";
import { useSessionExpiry } from "../lib/use-session-expiry";
import { formatCountdown, type SessionWarning } from "../lib/session-expiry";
import { Button } from "./ui/button";

/**
 * The announcement of the session's end ([NF-008], [AC-062]): visible
 * `SESSION_WARNING_LEAD` before whichever edge comes first.
 *
 * Non-blocking on purpose: a person in the middle of a postmortem is exactly
 * who the warning protects, and a modal would interrupt that work. Not
 * colour-alone - amber block PLUS clock icon PLUS a heading.
 *
 * The idle edge can be pushed back (the extend button); the absolute end
 * cannot - the banner then says when it comes and that a new login follows,
 * and offers no button that would pretend otherwise.
 */
export function SessionExpiryBanner(): JSX.Element | null {
  const { status, isSystem } = useAuth();
  const { warning, extend } = useSessionExpiry(status === "authenticated");
  if (!warning) return null;
  return <SessionExpiryNotice warning={warning} system={isSystem} onExtend={extend} />;
}

/** The banner itself, given the warning - rendered alone in the unit test. */
export function SessionExpiryNotice({
  warning,
  system,
  onExtend,
}: {
  warning: SessionWarning;
  system: boolean;
  onExtend: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const countdown = formatCountdown(warning.secondsLeft);
  const body =
    warning.kind === "idle"
      ? t("session.idleBody", { countdown })
      : system
        ? t("session.systemBody", { countdown })
        : t("session.absoluteBody", { countdown });

  return (
    <div
      data-testid="session_expiry-banner"
      role="status"
      aria-live="polite"
      className="mb-4 flex flex-col gap-3 rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 sm:flex-row sm:items-center sm:justify-between dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
    >
      <div className="flex items-start gap-3">
        <Clock className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <div>
          <p data-testid="session_expiry-title" className="font-medium">
            {t("session.expiryTitle")}
          </p>
          <p data-testid="session_expiry-body" className="text-sm">
            {body}
          </p>
        </div>
      </div>
      {warning.kind === "idle" ? (
        <Button data-testid="session_extend" variant="outline" onClick={onExtend} className="sm:shrink-0">
          {t("session.extend")}
        </Button>
      ) : null}
    </div>
  );
}
